import React from 'react';
import { get, includes } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { LanguageSelector } from '@unite-us/app-components';
import { updateGlobalState } from 'actions/Global/globalActions';
import {
  showTranslationsNewLanguages,
  cl1511NHConsentMvp as cl1511NHConsentMvpSelector,
} from 'common/utils/FeatureFlags/flags';
import { env } from 'src/config/env';
import { NH_SET_TITLE, NH_LANGUAGE_ENUMS } from '../constants';
import '../stylesheets/informedConsent.scss';
import RequestOrUploadConsentForm from './RequestOrUploadConsentForm';

const buildConsentAppUrl = (
  contactId,
  contact,
  currentEmployee,
  submissionMethod,
  language,
  showNHConsent,
  sentReferrals,
) => {
  const params = {
    person_id: contactId,
    first_name: contact.first_name || '',
    last_name: contact.last_name || '',
    employee_id: currentEmployee.id,
    employee_fullname: currentEmployee.full_name || '',
    submission_method: submissionMethod,
    'form-only': '',
  };

  if (showNHConsent && sentReferrals.length > 0) {
    params.show_nh_consent = showNHConsent;
    params.sent_referrals = btoa(JSON.stringify(sentReferrals));
  }
  const urlParams = new URLSearchParams({ ...params, language });

  return `${env.CONSENT_APP_URL}?${urlParams.toString()}`;
};

class ConsentReferrals extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showConsentApp: false,
      showConsentAppAttestation: false,
      consentAppUrl: '',
      currentLanguage: props.language,
      field: {},
      registerField: null,
      title: 'Informed Consent',
    };

    this.hideConsent = this.hideConsent.bind(this);
    this.showConsent = this.showConsent.bind(this);
    this.setConsentAttestation = this.setConsentAttestation.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.getConsentAppUrl = this.getConsentAppUrl.bind(this);
    this.handleMessageEvent = this.handleMessageEvent.bind(this);
  }

  componentDidMount() {
    window.addEventListener('message', this.handleMessageEvent);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessageEvent);
  }

  handleOnChange(val, field, registerField) {
    const indexToReplace = this.state.consentAppUrl.lastIndexOf('language=') + 'language'.length;
    if (indexToReplace !== -1) {
      const trimURL = this.state.consentAppUrl.substring(0, indexToReplace);
      const newURL = trimURL.concat(`=${val}`);
      this.props.updateGlobalState({
        language: val,
      });
      const updatedField = { ...field, value: val };

      this.setState({
        consentAppUrl: newURL,
        currentLanguage: val,
        field: updatedField,
        registerField,
      });
    }
  }

  handleMessageEvent(event) {
    const trustedOrigin = get(event, 'isTrusted') && includes(this.state.consentAppUrl, get(event, 'origin'));
    const { key, title: newTitle } = get(event, 'data');
    const isNHSetTitle = key === NH_SET_TITLE && this.props.showNHConsent;

    if (trustedOrigin && isNHSetTitle && newTitle) {
      this.setState({ title: newTitle });
    }
  }

  getConsentAppUrl(groupId, contactId, contact, consentAttestation) {
    const {
      currentEmployee,
      showNHConsent,
      sentReferrals,
    } = this.props;

    if (!contactId) {
      return;
    }

    const submissionMethod = consentAttestation === true ? 'attestation' : '';
    const language = this.state.currentLanguage;
    const consentAppUrl = buildConsentAppUrl(
      contactId,
      contact,
      currentEmployee,
      submissionMethod,
      language,
      showNHConsent,
      sentReferrals,
    );

    this.setState({
      consentAppUrl,
      showConsentAppAttestation: consentAttestation === true,
    });
  }

  setConsentAttestation(value) {
    this.setState({ showConsentAppAttestation: value });
  }

  hideConsent() {
    this.setState({ title: 'Informed Consent', showConsentApp: false });
  }

  showConsent(field, registerField) {
    this.setState({
      showConsentApp: true,
      field,
      registerField,
    });
  }

  render() {
    const {
      contact,
      initializedValue,
      shouldShowTranslationsNewLanguages,
      sentReferrals,
      languageOptions,
    } = this.props;
    const {
      showConsentApp,
      consentAppUrl,
      field,
      registerField,
      showConsentAppAttestation,
      title,
    } = this.state;

    const Title = (showConsentApp && !showConsentAppAttestation) ? (
      <div className="flex justify-between items-center p-4 border-b border-solid border-dark-border-blue">
        <div className="pl-2 text-xl align-middle">{title}</div>
        <LanguageSelector
          onChange={this.handleOnChange}
          field={field}
          registerField={registerField}
          options={languageOptions}
          translationsNewLanguageFlag={shouldShowTranslationsNewLanguages}
        />
      </div>
    ) : (
      <div className="flex justify-between items-center p-4 border-b border-solid border-dark-border-blue">
        <div className="pl-2 text-xl align-middle">{title}</div>
      </div>
    );

    return (
      <div className="mt-10 bg-white rounded border border-solid border-dark-border-blue">
        {Title}
        <div className="p-4">
          <RequestOrUploadConsentForm
            contact={contact}
            contactId={contact.id}
            hideConsent={this.hideConsent}
            initializedValue={initializedValue}
            onNavigateAway={this.props.onNavigateAway}
            setConsentAttestation={this.setConsentAttestation}
            showConsent={this.showConsent}
            showConsentApp={this.state.showConsentApp}
            showConsentAppAttestation={this.state.showConsentAppAttestation}
            handleOnChange={this.handleOnChange}
            consentAppUrl={consentAppUrl}
            getConsentAppUrl={this.getConsentAppUrl}
            sentReferrals={sentReferrals}
          />
        </div>
      </div>
    );
  }
}

ConsentReferrals.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string,
    consent: PropTypes.string,
  }),
  currentEmployee: PropTypes.object.isRequired,
  initializedValue: PropTypes.string,
  onNavigateAway: PropTypes.func,
  shouldShowTranslationsNewLanguages: PropTypes.bool,
  language: PropTypes.string,
  updateGlobalState: PropTypes.func.isRequired,
  showNHConsent: PropTypes.bool.isRequired,
  sentReferrals: PropTypes.array,
  languageOptions: PropTypes.array,
};

ConsentReferrals.defaultProps = {
  contact: {},
  initializedValue: '',
  onNavigateAway: () => { },
  language: 'en',
  shouldShowTranslationsNewLanguages: false,
  sentReferrals: [],
  languageOptions: [],
};

function mapStateToProps(state, ownProps) {
  const { sentReferrals } = ownProps;
  const currentEmployee = state.globalState.currentEmployee;
  const nh_consent = state.globalState.currentProvider?.group?.features?.nh_consent ||
    state.session?.currentProvider?.features?.nh_consent;
  const showNHConsent = cl1511NHConsentMvpSelector(state) && nh_consent;

  const languageOptions = showNHConsent && (sentReferrals || []).length > 0 ? NH_LANGUAGE_ENUMS : [];
  const language = languageOptions.find((option) => option.value === state.globalState.language)?.value || 'en';

  return {
    language,
    languageOptions,
    shouldShowTranslationsNewLanguages: showTranslationsNewLanguages(state),
    showNHConsent,
    currentEmployee,
  };
}

export default connect(mapStateToProps, {
  updateGlobalState,
}, null, { forwardRef: true })(ConsentReferrals);
